import type { ApiError } from "@mola/contracts";

export class ApiRequestError extends Error {
  constructor(public readonly status: number, public readonly body: ApiError) {
    super(body.error.message);
  }
}

function cookieValue(name: string): string | undefined {
  return document.cookie.split("; ").find((value) => value.startsWith(`${name}=`))?.split("=").slice(1).join("=");
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (init.method && !["GET", "HEAD", "OPTIONS"].includes(init.method.toUpperCase()) && typeof navigator !== "undefined" && !navigator.onLine) {
    throw new Error("Offline — changes are disabled until Course Board reconnects.");
  }
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (init.method && !["GET", "HEAD", "OPTIONS"].includes(init.method.toUpperCase())) {
    const csrf = cookieValue("mola_csrf");
    if (csrf) headers.set("x-csrf-token", decodeURIComponent(csrf));
  }
  const response = await fetch(path, { ...init, headers, credentials: "same-origin" });
  if (!response.ok) {
    const body = await response.json() as ApiError;
    throw new ApiRequestError(response.status, body);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export function download(path: string): void {
  window.location.assign(path);
}

export interface OfflineSnapshot<T> { value: T; cachedAt: string }

export function readOfflineSnapshot<T>(key: string): OfflineSnapshot<T> | null {
  try {
    return JSON.parse(localStorage.getItem(`mola-offline:${key}`) ?? "null") as OfflineSnapshot<T> | null;
  } catch {
    return null;
  }
}

export function writeOfflineSnapshot(key: string, value: unknown): void {
  localStorage.setItem(`mola-offline:${key}`, JSON.stringify({ value, cachedAt: new Date().toISOString() }));
}

export function clearOfflineSnapshots(): void {
  for (const key of Object.keys(localStorage)) if (key.startsWith("mola-offline:")) localStorage.removeItem(key);
}
